import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { scrollToId } from "./Reveal";

export default function FinalCTA() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section ref={ref} className="relative flex min-h-[82vh] items-center overflow-hidden" data-testid="final-cta">
      <motion.div style={{ y }} className="absolute inset-0 scale-125">
        <img
          src="https://images.pexels.com/photos/2766338/pexels-photo-2766338.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Cinematic destination landscape"
          loading="lazy"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-ink/75 via-ink/60 to-ink/85" />
      <div className="container-x relative py-28 text-center">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="eyebrow"
        >
          Travel-Tek · Run. Distribute. Grow.
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
          className="mx-auto mt-6 max-w-3xl font-serif text-4xl leading-[1.1] tracking-tight text-paper sm:text-5xl lg:text-6xl"
        >
          Your destination already has the supply. Now build the digital business around it.
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.28 }}
          className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-paper/70 sm:text-lg"
        >
          Launch your branded destination platform, centralize your operations and
          create new ways to sell.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.42 }}
          className="mt-10 flex flex-wrap items-center justify-center gap-4"
        >
          <button
            onClick={scrollToId("#demo")}
            data-testid="finalcta-book-demo-btn"
            className="group inline-flex items-center gap-2 rounded-full bg-paper px-8 py-4 text-sm font-semibold text-ink transition-colors duration-300 hover:bg-terra"
          >
            Book a Demo
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <a
            href="/platform"
            data-testid="finalcta-explore-btn"
            className="inline-flex items-center gap-2 rounded-full border border-paper/40 px-8 py-4 text-sm font-semibold text-paper transition-colors duration-300 hover:bg-paper hover:text-ink"
          >
            Explore the Platform
          </a>
        </motion.div>
      </div>
    </section>
  );
}
